import { z } from 'zod';

// Level mapping runs of a Position Architecture (backend PRO-2307). The api is camelCase,
// unlike the snake_case Position endpoints.

export const levelMappingRunStatusSchema = z.enum([
    'QUEUED',
    'RUNNING',
    'DONE',
    'FAILED',
    'CANCELLED',
    'SUPERSEDED',
    'APPLYING',
    'APPROVED',
    'APPLY_FAILED',
]);

export type LevelMappingRunStatus = z.infer<typeof levelMappingRunStatusSchema>;

/** ACCEPTED was mapped automatically, CHOOSE has ranked suggestions, MANUAL has none. */
export const levelMappingBucketSchema = z.enum(['ACCEPTED', 'CHOOSE', 'MANUAL']);

export type LevelMappingBucket = z.infer<typeof levelMappingBucketSchema>;

const countSchema = z.number().int().nonnegative().catch(0);

export const levelMappingBucketCountsSchema = z.object({
    accepted: countSchema,
    choose: countSchema,
    manual: countSchema,
});

export type LevelMappingBucketCounts = z.infer<typeof levelMappingBucketCountsSchema>;

const actionCountSchema = z.object({ positions: countSchema, units: countSchema });

export const levelMappingApprovalSummarySchema = z.object({
    actions: z.record(z.string(), actionCountSchema).default({}),
});

export const levelMappingApprovalResultSchema = z.object({
    keptAuto: countSchema,
    leftUnmapped: countSchema,
    mappedNew: countSchema,
    removed: countSchema,
    replaced: countSchema,
    skippedCount: countSchema,
});

export type LevelMappingApprovalResult = z.infer<typeof levelMappingApprovalResultSchema>;

export const levelMappingRunSchema = z.object({
    id: z.string(),
    architectureId: z.string(),
    jobArchitectureId: z.string(),
    status: levelMappingRunStatusSchema,
    unitsDone: countSchema,
    unitsTotal: z.number().int().nullish(),
    buckets: levelMappingBucketCountsSchema.default({ accepted: 0, choose: 0, manual: 0 }),
    autoMappedPositions: countSchema,
    planned: levelMappingApprovalSummarySchema.nullish(),
    result: levelMappingApprovalResultSchema.nullish(),
    error: z.object({ code: z.string(), message: z.string() }).nullish(),
    supersededBy: z.string().nullish(),
    createdAt: z.string(),
    finishedAt: z.string().nullish(),
});

export type LevelMappingRun = z.infer<typeof levelMappingRunSchema>;

export const levelMappingSuggestionSchema = z.object({
    levelItemId: z.string(),
    roleItemId: z.string(),
    probability: z.number(),
});

export type LevelMappingSuggestion = z.infer<typeof levelMappingSuggestionSchema>;

export const levelMappingUnitSchema = z.object({
    unitId: z.string(),
    ordinal: z.number().int(),
    title: z.string(),
    department: z.string().nullish(),
    legalEntity: z.string().nullish(),
    legalEntityValueId: z.string().nullish(),
    headcount: z.number().int().nonnegative(),
    bucket: levelMappingBucketSchema.nullish(),
    confidence: z.number().nullish(),
    defaultLevelItemId: z.string().nullish(),
    suggestions: levelMappingSuggestionSchema.array().default([]),
    visibleSuggestions: countSchema,
});

export type LevelMappingUnit = z.infer<typeof levelMappingUnitSchema>;

export const levelMappingUnitPageSchema = z.object({
    data: levelMappingUnitSchema.array().default([]),
    pagination: z.object({
        count: z.number().int(),
        page: z.number().int(),
        pages: z.number().int(),
        size: z.number().int(),
    }),
});

export type LevelMappingUnitPage = z.infer<typeof levelMappingUnitPageSchema>;

/** Returned with 409 when another run with different inputs is active. */
export const levelMappingActiveRunSchema = z.object({ activeRunId: z.string() });

export const levelMappingApprovalResponseSchema = z.object({
    runId: z.string(),
    status: levelMappingRunStatusSchema,
    result: levelMappingApprovalResultSchema.nullish(),
});

export type LevelMappingApprovalResponse = z.infer<typeof levelMappingApprovalResponseSchema>;

export type LevelMappingStartRequest = {
    readonly departmentAttributeId: string;
    readonly jobArchitectureId: string;
    readonly legalEntityAttributeId: string;
    readonly locationAttributeId?: string;
};

/** A null level leaves the unit unmapped, or removes its automatic mapping. */
export type LevelMappingUnitChange = {
    readonly levelItemId: string | null;
    readonly unitId: string;
};
